import { useBoxHighlight } from 'features/knowledgeDocumentViewer/components/viewer/hooks/useBoxHighlight'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import {
  ActionMode,
  useKnowledgeDocumentUXContext,
} from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useBoxLinkedDocuments } from 'features/knowledgeDocumentViewer/hooks/useBoxLinkedDocuments'
import { shouldBoxBeDisabled } from 'features/knowledgeDocumentViewer/utils/bboxHelpers'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch'
import { DocumentViewerAssetType } from 'types'

import { useBoxStyles } from './hooks/useBoxStyles'
import { useFitToViewport } from './hooks/useFitToViewport'
import { useShiftDragSelection } from './hooks/useShiftDragSelection'
import { useZoomControls } from './hooks/useZoomControls'
import { OffPageConnectorTooltip } from './OffPageConnectorTooltip'
import {
  ImageContainer,
  MultipageImageContainer,
  OverlaySvg,
  StyledImage,
  Tooltip,
  TooltipLabel,
  TooltipType,
  ViewerContainer,
} from './styles'
import { Box, ImageViewerHandle } from './types'

export type ImageViewerProps = {
  imageUrls: string[]
  boxes: Box[]
  onBoxClick?: (box: Box, event: React.MouseEvent) => void
  onBoxAuxClick?: (box: Box, event: React.MouseEvent) => void
  onBoxContextMenu?: (box: Box, event: React.MouseEvent) => void
  onBoxHover?: (box: Box | null) => void
  onMultipleBoxSelect?: (boxIds: string[]) => void
  onResetZoom?: () => void
  onZoomStateChange?: (state: {
    isAtMinZoom: boolean
    isAtMaxZoom: boolean
  }) => void
  highlightBoxIds: string[]
  actionMode: ActionMode
  showTooltips?: boolean
  isLoading?: boolean
}

export const ImageViewerWithOverlay = forwardRef<
  ImageViewerHandle,
  ImageViewerProps
>(function ImageViewerWithOverlay(
  {
    imageUrls,
    boxes,
    onBoxClick,
    onBoxAuxClick,
    onBoxContextMenu,
    onBoxHover,
    onMultipleBoxSelect,
    onResetZoom,
    onZoomStateChange,
    highlightBoxIds,
    actionMode,
    showTooltips = false,
  },
  ref
) {
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null)
  const svgRef = useRef<SVGSVGElement | null>(null)
  const viewerContainerRef = useRef<HTMLDivElement | null>(null)
  const [minScale, setMinScale] = useState(0.05)
  const maxScale = 3
  const [tooltipBox, setTooltipBox] = useState<Box | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState<{
    x: number
    y: number
  } | null>(null)
  const [imageDimensions, setImageDimensions] = useState<
    [{ w: number; h: number }, { w: number; h: number }]
  >([
    { w: 0, h: 0 },
    { w: 0, h: 0 },
  ])
  const isMultipage = imageUrls.length > 1

  const [internalHighlightedBoxIds, setInternalHighlightedBoxIds] =
    useBoxHighlight(highlightBoxIds)

  const {
    siteID,
    knowledgeDocumentEntriesForSelectedDocument,
    systemDocumentEntries,
  } = useKnowledgeDocumentDataContext()

  const {
    activeTabId,
    currentSelectedBoxIds,
    updateDocumentHistoryItem,
    hoverId,
    handleMouseEnter: contextHandleMouseEnter,
    handleMouseLeave: contextHandleMouseLeave,
  } = useKnowledgeDocumentUXContext()

  const handleMouseEnter = useCallback(
    (box: Box) => {
      contextHandleMouseEnter(box)
      onBoxHover?.(box)
    },
    [contextHandleMouseEnter, onBoxHover]
  )

  const handleMouseLeave = useCallback(() => {
    contextHandleMouseLeave()
    onBoxHover?.(null)
  }, [contextHandleMouseLeave, onBoxHover])

  const { strokeFor, fillFor } = useBoxStyles(
    hoverId,
    internalHighlightedBoxIds,
    actionMode ?? ActionMode.None
  )

  const handleBackgroundClick = useCallback(() => {
    if (currentSelectedBoxIds.length > 0) {
      updateDocumentHistoryItem(activeTabId, { selectedBoxIds: [] })
    }
  }, [activeTabId, currentSelectedBoxIds, updateDocumentHistoryItem])

  const { connectedKnowledgeDocumentEntries } = useBoxLinkedDocuments(
    knowledgeDocumentEntriesForSelectedDocument,
    tooltipBox,
    siteID
  )

  const tooltipLinkedCount = useMemo(() => {
    return connectedKnowledgeDocumentEntries?.length || 0
  }, [connectedKnowledgeDocumentEntries])

  const handleBoxMouseEnter = useCallback(
    (box: Box, evt: React.MouseEvent<SVGRectElement, MouseEvent>) => {
      handleMouseEnter(box)
      if (showTooltips && viewerContainerRef.current) {
        setTooltipBox(box)
        const containerRect = viewerContainerRef.current.getBoundingClientRect()
        setTooltipPosition({
          x: evt.clientX - containerRect.left,
          y: evt.clientY - containerRect.top,
        })
      }
    },
    [handleMouseEnter, showTooltips]
  )

  const handleBoxMouseLeave = useCallback(() => {
    handleMouseLeave()
    if (showTooltips) {
      setTooltipBox(null)
      setTooltipPosition(null)
    }
  }, [handleMouseLeave, showTooltips])

  const combinedDimensions = useMemo(() => {
    if (!isMultipage) {
      return imageDimensions[0]
    }

    const totalWidth = imageDimensions[0].w + imageDimensions[1].w
    const maxHeight = Math.max(imageDimensions[0].h, imageDimensions[1].h)
    return { w: totalWidth, h: maxHeight }
  }, [isMultipage, imageDimensions])

  const adjustedBoxes = useMemo(() => {
    if (!isMultipage) {
      return boxes
    }

    const firstPageWidth = imageDimensions[0].w
    return boxes.map((box) => {
      if (box.id.endsWith('-1')) {
        return {
          ...box,
          x: box.x + firstPageWidth,
        }
      }
      return box
    })
  }, [boxes, isMultipage, imageDimensions])

  // Show tooltip when hoverId is set externally (e.g., from table row hover)
  useEffect(() => {
    if (!showTooltips || !hoverId) {
      if (!hoverId && tooltipBox) {
        setTooltipBox(null)
        setTooltipPosition(null)
      }
      return
    }

    const matchingBox = adjustedBoxes.find((b) => b.id === hoverId)
    if (!matchingBox) return

    if (tooltipBox?.id === matchingBox.id) return

    if (viewerContainerRef.current && transformRef.current) {
      const transform = transformRef.current.instance.transformState
      const containerRect = viewerContainerRef.current.getBoundingClientRect()

      const boxCenterX = (matchingBox.x + matchingBox.w / 2) * transform.scale
      const boxTopY = matchingBox.y * transform.scale

      const screenX = boxCenterX + transform.positionX
      const screenY = boxTopY + transform.positionY

      if (
        screenX >= 0 &&
        screenX <= containerRect.width &&
        screenY >= 0 &&
        screenY <= containerRect.height
      ) {
        setTooltipBox(matchingBox)
        setTooltipPosition({ x: screenX, y: screenY })
      }
    }
  }, [hoverId, adjustedBoxes, showTooltips, tooltipBox])

  const handleImageLoad = useCallback(
    (index: number) => (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { naturalWidth, naturalHeight } = e.currentTarget
      setImageDimensions((prev) => {
        return {
          ...prev,
          [index]: { w: naturalWidth, h: naturalHeight },
        }
      })
    },
    []
  )

  const allImagesLoaded = useMemo(() => {
    if (!isMultipage) {
      return imageDimensions[0].w > 0 && imageDimensions[0].h > 0
    } else if (isMultipage) {
      return (
        imageDimensions[0].w > 0 &&
        imageDimensions[0].h > 0 &&
        imageDimensions[1].w > 0 &&
        imageDimensions[1].h > 0
      )
    }
    return false
  }, [imageDimensions, isMultipage])

  const { imgNatural, resetToFit, onFitComplete } = useFitToViewport(
    transformRef,
    setMinScale,
    combinedDimensions,
    isMultipage,
    allImagesLoaded,
    8
  )

  const { zoomToBox, resetZoom } = useZoomControls(
    adjustedBoxes,
    imgNatural,
    transformRef,
    resetToFit,
    onResetZoom
  )

  const applyZoom = useCallback(
    (calculateNewScale: (currentScale: number) => number) => {
      if (!transformRef.current) return
      const wrapper = transformRef.current.instance.wrapperComponent
      if (!wrapper) return
      const wrapRect = wrapper.getBoundingClientRect()
      const currentState = transformRef.current.instance.transformState
      const newScale = calculateNewScale(currentState.scale)

      // Zoom from center of viewport
      const centerX = wrapRect.width / 2
      const centerY = wrapRect.height / 2

      // Calculate new position to keep center point fixed
      const scaleDiff = newScale / currentState.scale
      const newPositionX =
        centerX - (centerX - currentState.positionX) * scaleDiff
      const newPositionY =
        centerY - (centerY - currentState.positionY) * scaleDiff

      transformRef.current.setTransform(
        newPositionX,
        newPositionY,
        newScale,
        200
      )
    },
    []
  )

  const zoomIn = useCallback(() => {
    applyZoom((currentScale) => Math.min(currentScale * 2.0, 10)) // Max scale is 10
  }, [applyZoom])

  const zoomOut = useCallback(() => {
    applyZoom((currentScale) => Math.max(currentScale / 2.0, minScale)) // Min scale is minScale
  }, [applyZoom, minScale])

  const {
    isShiftPressed,
    isDragging,
    selectionBox,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  } = useShiftDragSelection({
    svgRef,
    boxes: adjustedBoxes,
    onSelectionComplete: (selectedIds) => {
      onMultipleBoxSelect?.(selectedIds)
    },
    imageNaturalDimensions: imgNatural,
  })

  // Check and notify zoom state changes
  const checkAndNotifyZoomState = useCallback(() => {
    if (!transformRef.current || !onZoomStateChange) return

    const currentState = transformRef.current.instance.transformState
    const epsilon = 0.001

    const isAtMin = Math.abs(currentState.scale - minScale) < epsilon
    const isAtMax =
      Math.abs(currentState.scale - maxScale) < epsilon ||
      currentState.scale >= maxScale

    onZoomStateChange({ isAtMinZoom: isAtMin, isAtMaxZoom: isAtMax })
  }, [minScale, maxScale, onZoomStateChange])

  // Check zoom state when minScale changes (affects isAtMinZoom)
  useEffect(() => {
    checkAndNotifyZoomState()
  }, [minScale, checkAndNotifyZoomState])

  useImperativeHandle(
    ref,
    () => ({
      zoomToBox,
      setHighlightedBoxIds: setInternalHighlightedBoxIds,
      resetZoom,
      zoomIn,
      zoomOut,
    }),
    [zoomToBox, resetZoom, zoomIn, zoomOut, setInternalHighlightedBoxIds]
  )

  return (
    <ViewerContainer
      ref={viewerContainerRef}
      style={{ cursor: isShiftPressed ? 'crosshair' : 'default' }}>
      <TransformWrapper
        ref={transformRef}
        minScale={minScale}
        maxScale={10}
        limitToBounds
        wheel={{ step: 0.5 }}
        doubleClick={{ disabled: false, mode: 'zoomIn' }}
        pinch={{ disabled: false }}
        alignmentAnimation={{ sizeX: 100, sizeY: 100 }}
        onTransformed={() => {
          checkAndNotifyZoomState()
        }}>
        <TransformComponent
          wrapperStyle={{ width: '100%', height: '100%' }}
          contentStyle={{ display: 'inline-block' }}>
          <ImageContainer
            $opacity={onFitComplete ? 1 : 0}
            $width={imgNatural ? `${imgNatural.w}px` : 'auto'}
            $height={imgNatural ? `${imgNatural.h}px` : 'auto'}>
            {isMultipage ? (
              <MultipageImageContainer>
                {imageUrls.map((url, index) => (
                  <StyledImage
                    key={index}
                    src={url}
                    alt={`diagram page ${index + 1}`}
                    onLoad={handleImageLoad(index)}
                    draggable={false}
                  />
                ))}
              </MultipageImageContainer>
            ) : (
              <StyledImage
                src={imageUrls[0]}
                alt='diagram'
                onLoad={handleImageLoad(0)}
                draggable={false}
              />
            )}

            {!!imgNatural && (
              <OverlaySvg
                ref={svgRef}
                role='presentation'
                viewBox={`0 0 ${imgNatural.w} ${imgNatural.h}`}
                preserveAspectRatio='none'
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}>
                <g>
                  {/* Background rect to capture clicks on empty space */}
                  <rect
                    x={0}
                    y={0}
                    width={imgNatural.w}
                    height={imgNatural.h}
                    fill='transparent'
                    style={{ pointerEvents: 'auto' }}
                    onClick={(evt) => {
                      if (evt.target === evt.currentTarget) {
                        handleBackgroundClick()
                      }
                    }}
                  />

                  {/* Page divider line for multipage view - at boundary between pages */}
                  {isMultipage && imgNatural && (
                    <line
                      x1={imageDimensions[0].w}
                      y1={0}
                      x2={imageDimensions[0].w}
                      y2={imgNatural.h}
                      stroke='black'
                      strokeWidth={2}
                      style={{ pointerEvents: 'none' }}
                    />
                  )}

                  {adjustedBoxes.map((b) => {
                    const isDisabled = shouldBoxBeDisabled(b, actionMode)
                    const expandBy = 2 // Pixels to expand box in all directions
                    const shiftX = 1 // Horizontal calibration offset in pixels
                    const shiftY = 0 // Vertical calibration offset in pixels
                    return (
                      <g key={b.id}>
                        <rect
                          x={b.x - expandBy + shiftX}
                          y={b.y - expandBy + shiftY}
                          width={b.w + expandBy * 2}
                          height={b.h + expandBy * 2}
                          rx={2}
                          ry={2}
                          fill={fillFor(b)}
                          stroke={strokeFor(b)}
                          strokeWidth={1.5}
                          style={{
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            pointerEvents: 'auto',
                            opacity: isDisabled ? 0.5 : 1,
                          }}
                          onMouseEnter={(evt) =>
                            !isDisabled && handleBoxMouseEnter(b, evt)
                          }
                          onMouseLeave={handleBoxMouseLeave}
                          onClick={(evt) => {
                            if (isDisabled) return
                            onBoxClick?.(b, evt)
                          }}
                          onAuxClick={(evt) => {
                            if (isDisabled) return
                            onBoxAuxClick?.(b, evt)
                          }}
                          onContextMenu={(evt) => {
                            if (isDisabled) return
                            onBoxContextMenu?.(b, evt)
                          }}
                        />
                      </g>
                    )
                  })}

                  {/* Selection box overlay */}
                  {isDragging && selectionBox && (
                    <rect
                      x={selectionBox.x}
                      y={selectionBox.y}
                      width={selectionBox.width}
                      height={selectionBox.height}
                      fill='rgba(59, 130, 246, 0.1)'
                      stroke='rgba(59, 130, 246, 0.8)'
                      strokeWidth={2}
                      strokeDasharray='5,5'
                      style={{
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                </g>
              </OverlaySvg>
            )}
          </ImageContainer>
        </TransformComponent>
      </TransformWrapper>
      {showTooltips && tooltipBox && tooltipPosition && (
        <Tooltip
          $left={tooltipPosition.x}
          $top={tooltipPosition.y - 10}
          $transform='translate(-50%, -100%)'>
          {tooltipBox.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR ? (
            <OffPageConnectorTooltip
              box={tooltipBox}
              systemDocumentEntries={systemDocumentEntries}
              linkedDocumentCount={tooltipLinkedCount}
            />
          ) : (
            <>
              <TooltipLabel>
                {tooltipBox.label || `Box ${tooltipBox.id}`}
              </TooltipLabel>
              {tooltipBox.type && (
                <TooltipType>Type: {tooltipBox.type}</TooltipType>
              )}
              {tooltipLinkedCount > 0 && (
                <TooltipType>
                  {tooltipLinkedCount}{' '}
                  {tooltipLinkedCount === 1 ? 'document' : 'documents'}
                </TooltipType>
              )}
            </>
          )}
        </Tooltip>
      )}
    </ViewerContainer>
  )
})

export default ImageViewerWithOverlay
